
import { useState, useRef, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { OrganizationRole } from '@/types/supabase';
import { toast } from 'sonner';

export const useOrganizations = (user: User | null) => {
  const [organizations, setOrganizations] = useState<OrganizationRole[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationRole | null>(null);
  
  // Add a ref to track if a fetch is in progress
  const isFetchingOrgs = useRef<boolean>(false);
  // Add a ref to track retry attempts
  const retryCount = useRef<number>(0);
  const MAX_RETRIES = 3;
  // Add a cache timestamp to limit frequent refreshes
  const lastRefreshTimestamp = useRef<number>(0);
  const REFRESH_COOLDOWN = 10000; // 10 seconds cooldown

  const refreshOrganizations = useCallback(async () => {
    if (!user) return;
    
    // Skip if a fetch is already in progress
    if (isFetchingOrgs.current) {
      console.log("Organizations fetch already in progress, skipping");
      return;
    }
    
    // Implement cooldown to prevent frequent refreshes
    const now = Date.now();
    if (now - lastRefreshTimestamp.current < REFRESH_COOLDOWN) {
      console.log("Skipping refresh due to cooldown period");
      return;
    }
    
    try {
      isFetchingOrgs.current = true;
      console.log("Fetching organizations...");
      
      // Enhanced query to get associated departments too
      const { data, error } = await supabase
        .from('user_organizations')
        .select(`
          id,
          role,
          access_level,
          department_id,
          organization:organizations (
            id,
            name
          )
        `);

      if (error) throw error;

      // Reset retry count on success
      retryCount.current = 0;
      
      // Make sure data is not null before proceeding
      if (!data) {
        console.warn("No organization data returned");
        return;
      }

      const formattedOrgs = data.map(item => ({
        id: item.id,
        role: item.role as 'primary_founder' | 'co_founder' | 'department_head' | 'team_member',
        access_level: item.access_level || 'standard',
        department_id: item.department_id,
        organization: item.organization
      })) as OrganizationRole[];

      setOrganizations(formattedOrgs);
      
      // Only set current org if it's not already set and there are orgs available
      if (!currentOrganization && formattedOrgs.length > 0) {
        setCurrentOrganization(formattedOrgs[0]);
      }
      
      // Update timestamp of successful refresh
      lastRefreshTimestamp.current = Date.now();
    } catch (error: any) {
      console.error('Error fetching organizations:', error.message);
      
      // Only retry if we haven't reached max retries
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current++;
        console.log(`Retry attempt ${retryCount.current} of ${MAX_RETRIES}`);
      }
    } finally {
      isFetchingOrgs.current = false;
    }
  }, [user, currentOrganization]);

  // Helper functions for role-based access control
  const isFounder = useCallback(() => {
    if (!currentOrganization) return false;
    return ['primary_founder', 'co_founder'].includes(currentOrganization.role);
  }, [currentOrganization]);

  const isPrimaryFounder = useCallback(() => {
    if (!currentOrganization) return false;
    return currentOrganization.role === 'primary_founder';
  }, [currentOrganization]);

  // Check if user has any of the required roles
  const userHasAccess = useCallback((requiredRoles: string[]) => {
    if (!currentOrganization) return false;
    return requiredRoles.includes(currentOrganization.role);
  }, [currentOrganization]);

  return {
    organizations,
    currentOrganization,
    setCurrentOrganization,
    refreshOrganizations,
    isFetchingOrgs,
    retryCount,
    MAX_RETRIES,
    isFounder,
    isPrimaryFounder,
    userHasAccess
  };
};
